import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { CopyIcon, UserPlusIcon } from 'lucide-react';

import { ApiError, api, type ConsoleUser, type Role } from '@/lib/api';
import { useDateTime, useT } from '@/lib/i18n';
import type { StringKey } from '@/lib/strings/en';
import { useCannotBecause } from '@/lib/permissions';
import { notify } from '@/lib/notify';
import { DataTable } from '@/components/data-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';

/**
 * Settings → Console users (todo §4).
 *
 * Replaces the placeholder that said user management was not built. Roles are
 * enforced server-side on every request; what this screen does is make the
 * enforcement administrable.
 *
 * THE GENERATED PASSWORD IS SHOWN ONCE. Only the hash is stored, so there is
 * nowhere to read it from afterwards — the dialog says so rather than letting an
 * admin close it and discover that later.
 */

/**
 * Least authority first. Not `Role`'s declaration order and not alphabetical —
 * this is the order a chooser should offer them in, so the default reading order
 * does not lead with the most powerful option.
 *
 * Written out rather than derived because a *presentation* order is a judgement
 * about this screen, not a fact about the contract. It is three short entries
 * and the typecheck fails if a role stops existing.
 */
const ROLES: readonly Role[] = ['analyst', 'psychometrician', 'admin'];

const roleLabel = (role: Role): StringKey => `role.${role}` as StringKey;

function CreateUserDialog({ onCreated }: { onCreated: () => void }) {
  const t = useT();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('analyst');
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<{ email: string; password: string } | null>(null);

  const create = useMutation({
    mutationFn: () => api.createUser({ email: email.trim(), role }),
    onSuccess: (user) => {
      setError(null);
      if (user.generatedPassword) {
        setIssued({ email: user.email, password: user.generatedPassword });
      }
      setEmail('');
      onCreated();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : t('users.createFailed')),
  });

  if (issued) {
    return (
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('users.created.title')}</DialogTitle>
          <DialogDescription>{t('users.created.description')}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-2">
          <Label>{issued.email}</Label>
          <div className="flex gap-2">
            <Input readOnly value={issued.password} className="font-mono" dir="ltr" />
            <Button
              variant="outline"
              onClick={() => {
                void navigator.clipboard?.writeText(issued.password);
                notify.success(t('common.copied'));
              }}
            >
              <CopyIcon className="size-4" />
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={() => setIssued(null)}>{t('common.done')}</Button>
        </DialogFooter>
      </DialogContent>
    );
  }

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{t('users.new.title')}</DialogTitle>
        <DialogDescription>{t('users.new.description')}</DialogDescription>
      </DialogHeader>

      <div className="grid gap-4">
        <div className="grid gap-2">
          <Label htmlFor="user-email">{t('login.email')}</Label>
          <Input
            id="user-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="operator@etcc.gov.ae"
          />
        </div>

        <div className="grid gap-2">
          <Label htmlFor="user-role">{t('profile.role')}</Label>
          <Select value={role} onValueChange={(v) => setRole(v as Role)}>
            <SelectTrigger id="user-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              {/* Least authority first, so the default reading order does not
                  present `admin` as the obvious choice. */}
              {ROLES.map((r) => (
                <SelectItem key={r} value={r}>{t(`users.role.${r}` as StringKey)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {error && (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </div>

      <DialogFooter>
        <Button onClick={() => create.mutate()} disabled={!email.trim() || create.isPending}>
          {create.isPending ? t('users.creating') : t('users.create')}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

export function ConsoleUsers() {
  const queryClient = useQueryClient();
  const t = useT();
  const dateTime = useDateTime();
  const [open, setOpen] = useState(false);
  const users = useQuery({ queryKey: ['users'], queryFn: api.users });
  // The LIST is readable by anyone signed in — knowing who the operators are is
  // not a privilege. Changing them is `platform:manage`.
  const cannotManage = useCannotBecause('platform:manage');

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['users'] });

  const update = useMutation({
    mutationFn: ({ userId, patch }: { userId: string; patch: { role?: Role; active?: boolean } }) =>
      api.updateUser(userId, patch),
    onSuccess: (user) => {
      notify.success(
        t('users.updated'),
        t('users.updated.detail', {
          email: user.email,
          role: t(roleLabel(user.role)),
          status: t(user.active ? 'users.status.active' : 'users.status.deactivated'),
        }),
      );
      invalidate();
    },
    onError: (err) =>
      // The server refuses to strand the platform without an administrator;
      // surface its reason rather than a generic failure.
      notify.error(
        t('users.updateFailed'),
        err instanceof ApiError ? err.message : t('table.errorDescription'),
      ),
  });

  const columns = useMemo<ColumnDef<ConsoleUser, unknown>[]>(
    () => [
      {
        header: t('users.column.user'),
        accessorKey: 'email',
        cell: ({ row }) => (
          <div>
            <p className="font-medium">{row.original.name}</p>
            <p className="text-muted-foreground text-sm">{row.original.email}</p>
          </div>
        ),
      },
      {
        header: t('profile.role'),
        accessorKey: 'role',
        cell: ({ row }) => (
          <Badge variant={row.original.role === 'admin' ? 'default' : 'secondary'}>
            {t(roleLabel(row.original.role))}
          </Badge>
        ),
      },
      {
        header: t('questions.column.status'),
        accessorKey: 'active',
        cell: ({ row }) => (
          <Badge variant={row.original.active ? 'default' : 'destructive'}>
            {t(row.original.active ? 'users.status.active' : 'users.status.deactivated')}
          </Badge>
        ),
      },
      {
        header: t('users.column.lastSignIn'),
        accessorKey: 'lastLoginAt',
        cell: ({ row }) => (
          <span className="text-muted-foreground text-sm">
            {row.original.lastLoginAt ? dateTime(row.original.lastLoginAt) : t('users.never')}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const user = row.original;
          return (
            <div className="flex items-center justify-end gap-2">
              {/* A SELECT, not the Make-admin / Make-analyst toggle this used
                  to be. That button was a two-state switch standing in for a
                  two-value enum, and a third role turns it into a control that
                  cannot express its own domain — "Make admin" on a
                  psychometrician silently hides the other destination. */}
              <Select
                value={user.role}
                disabled={update.isPending}
                onValueChange={(next) =>
                  update.mutate({ userId: user.userId, patch: { role: next as Role } })
                }
              >
                <SelectTrigger size="sm" className="w-44" aria-label={t('users.changeRole')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r} value={r}>{t(roleLabel(r))}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="ghost"
                disabled={update.isPending}
                onClick={() => update.mutate({ userId: user.userId, patch: { active: !user.active } })}
              >
                {t(user.active ? 'users.deactivate' : 'users.reactivate')}
              </Button>
            </div>
          );
        },
      },
    ],
    [update, t, dateTime, cannotManage],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-2">
        <p className="text-muted-foreground text-sm">{t('users.enforcementNote')}</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" disabled={cannotManage !== null} title={cannotManage ?? undefined}>
              <UserPlusIcon className="size-4" />
              {t('users.newUser')}
            </Button>
          </DialogTrigger>
          <CreateUserDialog onCreated={invalidate} />
        </Dialog>
      </div>

      <DataTable
        columns={columns}
        rows={users.data?.items ?? []}
        isLoading={users.isLoading}
        error={users.error}
        onRetry={() => void users.refetch()}
        emptyTitle={t('users.empty.title')}
        emptyDescription={t('users.empty.description')}
      />
    </div>
  );
}
